/**
 * LoginPage.tsx — login da SPA via POST /api/v1/auth/login.
 * Réplica visual do login.html original: painel gradiente + card de vidro.
 * A sessão é o MESMO cookie do Jinja: logar aqui vale nos dois mundos.
 */

import {
  faChartLine,
  faEnvelope,
  faEye,
  faEyeSlash,
  faGavel,
  faLock,
  faSearchDollar,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { login, loginGoogle, provedores, type Sessao } from '../../api/auth'
import { ApiError } from '../../api/http'
import { BotaoGoogle } from './BotaoGoogle'

export function LoginPage() {
  const [email, setEmail] = useState('')
  const [senha, setSenha] = useState('')
  const [lembrar, setLembrar] = useState(false)
  const [senhaVisivel, setSenhaVisivel] = useState(false)
  const [parametros] = useSearchParams()
  const navegar = useNavigate()
  const clienteQuery = useQueryClient()

  // O destino é o MESMO para as duas portas de entrada: quem entra por
  // senha e quem entra pelo Google recebe a mesma `Sessao` do backend.
  function aoEntrar(sessao: Sessao) {
    clienteQuery.setQueryData(['sessao'], sessao)
    const proxima = parametros.get('next')
    if (proxima) {
      navegar(proxima, { replace: true })
      return
    }
    // O backend decide o destino: o super admin sem prefeitura escolhida
    // vai ao painel (regra do login Jinja), que ainda não está na SPA e
    // por isso exige navegação de página inteira.
    const destino = sessao.destino_inicial
    if (destino.startsWith('/app/')) {
      navegar(destino.slice('/app'.length), { replace: true })
    } else {
      window.location.href = destino
    }
  }

  const mutacao = useMutation({
    mutationFn: () => login(email, senha, lembrar),
    onSuccess: aoEntrar,
  })

  // Só desenha o botão do Google se este ambiente tiver Client ID: sem isso
  // seria um botão que não funciona.
  const { data: provedoresDisponiveis } = useQuery({
    queryKey: ['auth-provedores'],
    queryFn: provedores,
    staleTime: Infinity,
    retry: false,
  })

  const mutacaoGoogle = useMutation({
    mutationFn: (credential: string) => loginGoogle(credential),
    onSuccess: aoEntrar,
  })

  function aoEnviar(evento: FormEvent) {
    evento.preventDefault()
    mutacao.mutate()
  }

  const falha = mutacao.error ?? mutacaoGoogle.error
  const erro =
    falha instanceof ApiError
      ? falha.message
      : mutacao.error
        ? 'Erro inesperado. Tente novamente.'
        : null

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-indigo-600 to-indigo-950 p-4">
      <div className="w-full max-w-4xl overflow-hidden rounded-2xl bg-white/95 shadow-2xl backdrop-blur">
        <div className="flex flex-col md:flex-row">
          <div className="relative flex flex-col justify-center bg-gradient-to-br from-indigo-600 to-purple-900 p-12 text-white md:w-1/2">
            <div className="absolute inset-0 z-0 bg-black opacity-10" />
            <div className="relative z-10 text-center">
              <img
                src="/static/logo-removido.png"
                alt="LicitIA"
                className="mx-auto mb-4 h-32 w-32 rounded-lg"
              />
              <p className="mt-8 text-lg opacity-90">
                Análise com IA para licitações e auditoria fiscal pública.
              </p>
              <div className="mt-12 space-y-4 text-left">
                <div className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faGavel} className="text-indigo-300" />
                  <p>Análise inteligente de licitações</p>
                </div>
                <div className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faSearchDollar} className="text-indigo-300" />
                  <p>Auditoria fiscal automatizada</p>
                </div>
                <div className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faChartLine} className="text-indigo-300" />
                  <p>Relatórios e insights estratégicos</p>
                </div>
              </div>
            </div>
          </div>

          <div className="p-10 md:w-1/2">
            {erro && (
              <div
                role="alert"
                className="mb-6 rounded-md border-l-4 border-red-500 bg-red-100 p-4 text-red-700"
              >
                <p>{erro}</p>
              </div>
            )}
            <div className="mb-10 text-center">
              <h2 className="text-3xl font-bold text-gray-800">Bem-vindo de volta</h2>
              <p className="mt-2 text-gray-600">Faça login para acessar sua conta</p>
            </div>

            <form onSubmit={aoEnviar} className="space-y-6">
              <div>
                <label htmlFor="login-email" className="font-medium text-gray-700">
                  E-mail
                </label>
                <div className="relative mt-1">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <FontAwesomeIcon icon={faEnvelope} className="text-gray-500" />
                  </div>
                  <input
                    id="login-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="seu e-mail"
                    className="w-full rounded-lg border border-gray-300 py-3 pl-10 pr-3 transition-transform focus:-translate-y-0.5 focus:border-transparent focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <label htmlFor="login-senha" className="font-medium text-gray-700">
                    Senha
                  </label>
                  <a
                    href="mailto:contato@licitia.net.br?subject=Recuperação de Senha"
                    className="text-sm text-indigo-600 hover:underline"
                  >
                    Esqueceu sua senha?
                  </a>
                </div>
                <div className="relative mt-1">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <FontAwesomeIcon icon={faLock} className="text-gray-500" />
                  </div>
                  <input
                    id="login-senha"
                    type={senhaVisivel ? 'text' : 'password'}
                    required
                    value={senha}
                    onChange={(e) => setSenha(e.target.value)}
                    placeholder="Sua senha"
                    className="w-full rounded-lg border border-gray-300 py-3 pl-10 pr-10 transition-transform focus:-translate-y-0.5 focus:border-transparent focus:ring-2 focus:ring-indigo-500"
                  />
                  <button
                    type="button"
                    onClick={() => setSenhaVisivel((v) => !v)}
                    aria-label={senhaVisivel ? 'Ocultar caracteres digitados' : 'Exibir caracteres digitados'}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-indigo-600"
                  >
                    <FontAwesomeIcon icon={senhaVisivel ? faEyeSlash : faEye} />
                  </button>
                </div>
              </div>

              <div className="flex items-center">
                <input
                  id="lembrar"
                  type="checkbox"
                  checked={lembrar}
                  onChange={(e) => setLembrar(e.target.checked)}
                  className="h-4 w-4 rounded text-indigo-600 focus:ring-indigo-500"
                />
                <label htmlFor="lembrar" className="ml-2 text-gray-700">
                  Lembrar-me
                </label>
              </div>

              <button
                type="submit"
                disabled={mutacao.isPending}
                className="mt-2 w-full rounded-lg bg-indigo-600 py-3 font-medium text-white transition duration-300 hover:scale-[1.02] hover:bg-purple-800 disabled:opacity-50"
              >
                {mutacao.isPending ? 'Entrando…' : 'Entrar no sistema'}
              </button>
            </form>

            {provedoresDisponiveis?.google.habilitado &&
              provedoresDisponiveis.google.client_id && (
                <>
                  <div className="my-6 flex items-center gap-3">
                    <span className="h-px flex-1 bg-gray-300" />
                    <span className="text-sm text-gray-500">ou</span>
                    <span className="h-px flex-1 bg-gray-300" />
                  </div>
                  <BotaoGoogle
                    clientId={provedoresDisponiveis.google.client_id}
                    desabilitado={mutacaoGoogle.isPending}
                    aoReceberCredencial={(credencial) =>
                      mutacaoGoogle.mutate(credencial)
                    }
                  />
                  <p className="mt-3 text-center text-xs text-gray-500">
                    Use a conta Google do seu e-mail já cadastrado no sistema.
                  </p>
                </>
              )}

            <p className="mt-8 text-center text-gray-600">
              Não tem uma conta?{' '}
              <a
                href="mailto:contato@licitia.net.br?subject=Solicitação de Acesso"
                className="font-medium text-indigo-600 hover:underline"
              >
                Solicite acesso
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
